// EntryDogForm.jsx - モーダル部分だけを使った「新規登録 / 編集」入力フォーム（出陳犬選択で編集）
// 依存: axios, @tanstack/react-query, react-calendar, japanese-holidays
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import JapaneseHolidays from 'japanese-holidays';
import './entrydogs.css';
import useNotificationStore from '../stores/notificationStore'; // 通知用

/* ========================================
 * 定数
 * ======================================== */
const API_URL = import.meta.env.VITE_API_URL;

// 登録番号種類
const REG_TYPE_OPTIONS = [
  { value: '', label: '選択してください' },
  { value: 'JKC', label: 'JKC' },
  { value: 'EXPEDITION', label: '遠征犬' },
];

/* ========================================
 * ヘルパー
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};

const formatInputDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/* ========================================
 * サブコンポーネント：日付ピッカー
 * ======================================== */
function DatePickerField({ value, onChange, placeholder, hasError }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeStartDate, setActiveStartDate] = useState(value ? new Date(value) : new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayValue = value ? formatDate(value) : '';

  const handleDateChange = (date) => {
    const formatted = formatInputDate(date);
    onChange({ target: { name: '', value: formatted } });
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: '', value: '' } });
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || JapaneseHolidays.isHoliday(date)) return 'ed-calSunday';
    if (dayOfWeek === 6) return 'ed-calSaturday';
    return null;
  };

  return (
    <div className="ed-datepicker" ref={containerRef}>
      <div className={`ed-datepickerInput ${hasError ? 'is-error' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <span className={displayValue ? '' : 'ed-datepickerPlaceholder'}>
          {displayValue || placeholder || '日付を選択'}
        </span>
        <div className="ed-datepickerActions">
          {value && (
            <button type="button" className="ed-datepickerClear" onClick={handleClear} title="クリア">
              ✕
            </button>
          )}
          <span className="ed-datepickerIcon">📅</span>
        </div>
      </div>

      {isOpen && (
        <div className="ed-datepickerPopup">
          <Calendar
            onChange={handleDateChange}
            value={value ? new Date(value) : null}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={({ activeStartDate }) => setActiveStartDate(activeStartDate)}
            view="month"
            minDetail="month"
            formatDay={(locale, date) => date.getDate()}
            formatShortWeekday={(locale, date) => ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]}
            calendarType="gregory"
            tileClassName={getTileClassName}
          />
        </div>
      )}
    </div>
  );
}

/* ========================================
 * メイン：出陳犬入力フォーム（新規 / 編集）
 * - 上部で「新規登録 / 編集」を切替
 * - 編集の場合、既存の出陳犬を選択するとフォームに反映
 * ======================================== */
export default function EntryDogForm() {
  const queryClient = useQueryClient();

  // モード: create / edit
  const [selectedEntryDogId, setSelectedEntryDogId] = useState('');
  const isEdit = selectedEntryDogId !== '';

  const { notification, showNotification } = useNotificationStore();

  /* ===== 一覧取得（entrydogs / breed_groups / breed_options） ===== */
  const { data: queryData, isLoading, isError, error } = useQuery({
    queryKey: ['entrydog-form'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/enduser/entrydogs/`);
      if (!res.data?.success) throw new Error('一覧の取得に失敗しました');
      return {
        entrydogs:     res.data.entrydogs     || [],
        breed_groups:  res.data.breed_groups  || [],
        breed_options: res.data.breed_options || [],
      };
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const entrydogs     = queryData?.entrydogs     || [];
  const breed_groups  = queryData?.breed_groups  || [];
  const breed_options = queryData?.breed_options || [];

  // 編集対象の出陳犬（選択から導出）
  const editingEntryDog = useMemo(() => {
    if (!isEdit) return null;
    return entrydogs.find((dog) => String(dog.entry_dog_id) === String(selectedEntryDogId)) || null;
  }, [isEdit, selectedEntryDogId, entrydogs]);

  /* ===== 作成/更新 Mutation ===== */
  const createMutation = useMutation({
    mutationFn: async (entryDogData) => {
      const res = await axios.post(`${API_URL}/api/enduser/entrydogs/create/`, entryDogData);
      if (!res.data?.success) throw new Error(res.data?.message || '登録に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entrydog-form'] });
      queryClient.invalidateQueries({ queryKey: ['entrydog-management'] });
      showNotification('出陳犬を登録しました');
      setSelectedEntryDogId('');
    },
    onError: (err) => window.alert(err.message || '登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ entryDogId, entryDogData }) => {
      const res = await axios.patch(`${API_URL}/api/enduser/entrydogs/${entryDogId}/`, entryDogData);
      if (!res.data?.success) throw new Error(res.data?.message || '更新に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entrydog-form'] });
      queryClient.invalidateQueries({ queryKey: ['entrydog-management'] });
      showNotification('出陳犬を更新しました');
    },
    onError: (err) => {
      const message = err.response?.data?.message || err.message || '更新に失敗しました';
      window.alert(message);
    },
  });

  /* ===== フォーム State ===== */
  const [formData, setFormData] = useState({
    pedigree_name: '',
    call_name: '',
    breed_group: '',
    breed_option: '',
    gender: '',
    birth_date: '',
    reg_type: '',
    reg_no1: '',
    reg_no2: '',
    reg_free: '',
    microchip_no: '',
    rabies_vaccination_date: '',
    sire_name: '',
    dam_name: '',
    exhibitor_name: '',
    exhibitor_pref: '',
    handler_name: '',
    handler_pref: '',
  });

  // 現在選択されている犬種(option)の実体
  const selectedBreedOption = useMemo(() => {
    if (!formData.breed_option) return null;
    return breed_options.find(
      (o) => String(o.breed_option_id) === String(formData.breed_option)
    ) || null;
  }, [breed_options, formData.breed_option]);

  // グループ：犬種が選ばれている場合は、その犬種の所属グループだけに絞る
  const filteredBreedGroups = useMemo(() => {
    if (!selectedBreedOption) return breed_groups;
    return breed_groups.filter(
      (g) => String(g.breed_group_id) === String(selectedBreedOption.breed_group_id)
    );
  }, [breed_groups, selectedBreedOption]);

  // 犬種：グループが選ばれている場合は、そのグループの犬種だけに絞る
  const filteredBreedOptions = useMemo(() => {
    if (!formData.breed_group) return breed_options;
    return breed_options.filter(
      (o) => String(o.breed_group_id) === String(formData.breed_group)
    );
  }, [breed_options, formData.breed_group]);

  const [errors, setErrors] = useState({});

  // 新規（=未選択）になったらフォームを初期化
  useEffect(() => {
    if (isEdit) return;

    setErrors({});
    setFormData({
      pedigree_name: '',
      call_name: '',
      breed_group: '',
      breed_option: '',
      gender: '',
      birth_date: '',
      reg_type: '',
      reg_no1: '',
      reg_no2: '',
      reg_free: '',
      microchip_no: '',
      rabies_vaccination_date: '',
      sire_name: '',
      dam_name: '',
      exhibitor_name: '',
      exhibitor_pref: '',
      handler_name: '',
      handler_pref: '',
      champion_file_name: '',
    });
  }, [isEdit]);

  // 編集対象が変わったらフォームに反映
  useEffect(() => {
    if (!editingEntryDog) return;

    setFormData({
      pedigree_name:           editingEntryDog.pedigree_name           || '',
      call_name:               editingEntryDog.call_name               || '',
      breed_group:             editingEntryDog.breed_group             || '',
      breed_option:            editingEntryDog.breed_option            || '',
      gender:                  editingEntryDog.gender                  || '',
      birth_date:              formatInputDate(editingEntryDog.birth_date) || '',
      reg_type:                editingEntryDog.reg_type                || '',
      reg_no1:                 editingEntryDog.reg_no1                 || '',
      reg_no2:                 editingEntryDog.reg_no2                 || '',
      reg_free:                editingEntryDog.reg_free                || '',
      microchip_no:            editingEntryDog.microchip_no            || '',
      rabies_vaccination_date: formatInputDate(editingEntryDog.rabies_vaccination_date) || '',
      sire_name:               editingEntryDog.sire_name               || '',
      dam_name:                editingEntryDog.dam_name                || '',
      exhibitor_name:          editingEntryDog.exhibitor_name          || '',
      exhibitor_pref:          editingEntryDog.exhibitor_pref          || '',
      handler_name:            editingEntryDog.handler_name            || '',
      handler_pref:            editingEntryDog.handler_pref            || '',
      champion_file_name:      editingEntryDog.champion_file_name      || '',
    });

    setErrors({});
  }, [editingEntryDog]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      // ① グループを先に変えた場合
      if (name === 'breed_group') {
        const nextGroup = value;
        if (!nextGroup) return { ...prev, breed_group: '', breed_option: '' };

        const currentBreed = breed_options.find(
          (o) => String(o.breed_option_id) === String(prev.breed_option)
        );
        const keepBreed =
          currentBreed && String(currentBreed.breed_group_id) === String(nextGroup);

        return {
          ...prev,
          breed_group:  nextGroup,
          breed_option: keepBreed ? prev.breed_option : '',
        };
      }

      // ② 犬種を先に変えた場合
      if (name === 'breed_option') {
        const nextBreedOptionId = value;
        if (!nextBreedOptionId) return { ...prev, breed_option: '' };

        const selected = breed_options.find(
          (o) => String(o.breed_option_id) === String(nextBreedOptionId)
        );
        if (!selected) return { ...prev, breed_option: nextBreedOptionId };

        return {
          ...prev,
          breed_option: nextBreedOptionId,
          breed_group:  String(selected.breed_group_id),
        };
      }

      // ③ その他
      return { ...prev, [name]: value };
    });

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.pedigree_name.trim()) newErrors.pedigree_name = '出陳犬名は必須です';
    if (!formData.call_name.trim())     newErrors.call_name     = 'コールネームは必須です';
    if (!formData.breed_group)          newErrors.breed_group   = '犬種グループ名は必須です';
    if (!formData.breed_option)         newErrors.breed_option  = '犬種名/バラエティー名は必須です';
    if (!formData.gender)               newErrors.gender        = '性別は必須です';
    if (!formData.birth_date)           newErrors.birth_date    = '生年月日は必須です';

    if (!formData.reg_type) {
      newErrors.reg_type = '登録番号種類は必須です';
    } else if (formData.reg_type === 'EXPEDITION') {
      if (!formData.reg_free.trim()) newErrors.reg_free = '登録番号（自由入力）は必須です';
    } else {
      if (!formData.reg_no1.trim()) newErrors.reg_no1 = '登録番号1は必須です';
      if (!formData.reg_no2.trim()) newErrors.reg_no2 = '登録番号2は必須です';
    }

    if (!formData.sire_name.trim())      newErrors.sire_name      = '父犬名は必須です';
    if (!formData.dam_name.trim())       newErrors.dam_name       = '母犬名は必須です';
    if (!formData.exhibitor_name.trim()) newErrors.exhibitor_name = '出陳者名は必須です';
    if (!formData.exhibitor_pref.trim()) newErrors.exhibitor_pref = '出陳者都道府県は必須です';
    if (!formData.handler_name.trim())   newErrors.handler_name   = '出陳取扱者名は必須です';
    if (!formData.handler_pref.trim())   newErrors.handler_pref   = '出陳取扱者都道府県は必須です';

    if (formData.reg_no1 && !/^\d{3}$/.test(formData.reg_no1)) {
      newErrors.reg_no1 = '登録番号(3桁)は3桁の数字で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const entryDogData = {
      ...formData,
      reg_no1:  formData.reg_type === 'EXPEDITION' ? '' : formData.reg_no1,
      reg_no2:  formData.reg_type === 'EXPEDITION' ? '' : formData.reg_no2,
      reg_free: formData.reg_type === 'EXPEDITION' ? formData.reg_free : '',
    };

    if (isEdit && editingEntryDog) {
      updateMutation.mutate({ entryDogId: editingEntryDog.entry_dog_id, entryDogData });
    } else {
      createMutation.mutate(entryDogData);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="ed-page">
      <h2 className="ed-pageTitle">出陳犬入力フォーム</h2>

      <section className="ed-section">
        {/* 登録/編集切替 */}
        <div className="ed-formHeader">
          <div className="ed-topSelectRow">
            <div className="ed-formGroup ed-formGroup--full">
              <label className="ed-label">登録/編集</label>
              <div className="ed-selectWrapper">
                <select
                  className="ed-select"
                  value={selectedEntryDogId}
                  onChange={(e) => setSelectedEntryDogId(e.target.value)}
                  disabled={isLoading || isError}
                >
                  <option value="">新規登録</option>
                  {entrydogs.map((dog) => (
                    <option key={dog.entry_dog_id} value={dog.entry_dog_id}>
                      {dog.reg_free
                        ? `${dog.reg_free} - ${dog.call_name}`
                        : `${dog.reg_no1} - ${dog.reg_no2} - ${dog.call_name}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {isError   && <div className="ed-error">{error?.message || '出陳犬一覧の取得に失敗しました'}</div>}
        {isLoading && <div className="ed-loading">読み込み中...</div>}

        {/* フォーム本体 */}
        {!isLoading && !isError && (
          <form onSubmit={handleSubmit} className="ed-formBody">

            {/* 基本情報 */}
            <div className="ed-formSection">
              <h3 className="ed-formSectionTitle">基本情報　※は必須です</h3>

              <div className="ed-formGrid">
                <div className="ed-formGroup">
                  <label className="ed-label">
                    出陳犬名（血統書名） <span className="ed-required">※</span>
                  </label>
                  <input
                    name="pedigree_name"
                    className={`ed-input ${errors.pedigree_name ? 'is-error' : ''}`}
                    value={formData.pedigree_name}
                    onChange={handleChange}
                  />
                  {errors.pedigree_name && <span className="ed-errorText">{errors.pedigree_name}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    コールネーム <span className="ed-required">※</span>
                  </label>
                  <input
                    name="call_name"
                    className={`ed-input ${errors.call_name ? 'is-error' : ''}`}
                    value={formData.call_name}
                    onChange={handleChange}
                  />
                  {errors.call_name && <span className="ed-errorText">{errors.call_name}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    犬種グループ名 <span className="ed-required">※</span>
                  </label>
                  <div className="ed-selectWrapper">
                    <select
                      name="breed_group"
                      className={`ed-select ${errors.breed_group ? 'is-error' : ''}`}
                      value={formData.breed_group}
                      onChange={handleChange}
                    >
                      <option value="">選択してください</option>
                      {filteredBreedGroups.map((g) => (
                        <option key={g.breed_group_id} value={g.breed_group_id}>
                          {g.grp_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.breed_group && <span className="ed-errorText">{errors.breed_group}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    犬種名／バラエティー名 <span className="ed-required">※</span>
                  </label>
                  <div className="ed-selectWrapper">
                    <select
                      name="breed_option"
                      className={`ed-select ${errors.breed_option ? 'is-error' : ''}`}
                      value={formData.breed_option}
                      onChange={handleChange}
                    >
                      <option value="">選択してください</option>
                      {filteredBreedOptions.map((o) => (
                        <option key={o.breed_option_id} value={o.breed_option_id}>
                          {o.variety_name ? `${o.breed_name}／${o.variety_name}` : o.breed_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.breed_option && <span className="ed-errorText">{errors.breed_option}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    性別 <span className="ed-required">※</span>
                  </label>
                  <div className="ed-selectWrapper">
                    <select
                      name="gender"
                      className={`ed-select ${errors.gender ? 'is-error' : ''}`}
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">選択してください</option>
                      <option value="M">オス</option>
                      <option value="F">メス</option>
                    </select>
                  </div>
                  {errors.gender && <span className="ed-errorText">{errors.gender}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    生年月日 <span className="ed-required">※</span>
                  </label>
                  <DatePickerField
                    value={formData.birth_date}
                    onChange={(e) => handleChange({ target: { name: 'birth_date', value: e.target.value } })}
                    placeholder="年/月/日"
                    hasError={!!errors.birth_date}
                  />
                  {errors.birth_date && <span className="ed-errorText">{errors.birth_date}</span>}
                </div>
              </div>
            </div>

            {/* 詳細情報 */}
            <div className="ed-formSection">
              <h3 className="ed-formSectionTitle">詳細情報</h3>

              <div className="ed-formGrid">
                {/* 登録番号種類 */}
                <div className="ed-formGroup">
                  <label className="ed-label">
                    登録番号種類 <span className="ed-required">※</span>
                  </label>
                  <div className="ed-selectWrapper">
                    <select
                      name="reg_type"
                      className={`ed-select ${errors.reg_type ? 'is-error' : ''}`}
                      value={formData.reg_type}
                      onChange={handleChange}
                    >
                      {REG_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.reg_type && <span className="ed-errorText">{errors.reg_type}</span>}
                </div>

                {/* 登録番号（reg_typeで切替） */}
                {formData.reg_type === 'EXPEDITION' ? (
                  <div className="ed-formGroup">
                    <label className="ed-label">登録番号 <span className="ed-required">※</span></label>
                    <input
                      name="reg_free"
                      className="ed-input"
                      value={formData.reg_free}
                      onChange={handleChange}
                      placeholder="自由入力"
                    />
                  </div>
                ) : (
                  <div className="ed-formGroup">
                    <label className="ed-label">登録番号 <span className="ed-required">※</span></label>
                    <div className="ed-regNoRow">
                      <input
                        name="reg_no1"
                        maxLength={3}
                        className={`ed-input ${errors.reg_no1 ? 'is-error' : ''}`}
                        value={formData.reg_no1}
                        onChange={handleChange}
                        placeholder="登録番号1(2桁or3桁)"
                      />
                      <span className="ed-regNoSep">-</span>
                      <input
                        name="reg_no2"
                        maxLength={15}
                        className={`ed-input ${errors.reg_no2 ? 'is-error' : ''}`}
                        value={formData.reg_no2}
                        onChange={handleChange}
                        placeholder="登録番号2(15桁以内)"
                      />
                    </div>
                    {(errors.reg_no1 || errors.reg_no2) && (
                      <span className="ed-errorText">{errors.reg_no1 || errors.reg_no2}</span>
                    )}
                  </div>
                )}

                <div className="ed-formGroup">
                  <label className="ed-label">個体識別番号</label>
                  <input
                    name="microchip_no"
                    className="ed-input"
                    value={formData.microchip_no}
                    onChange={handleChange}
                  />
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">狂犬病予防接種日</label>
                  <DatePickerField
                    value={formData.rabies_vaccination_date}
                    onChange={(e) =>
                      handleChange({ target: { name: 'rabies_vaccination_date', value: e.target.value } })
                    }
                    placeholder="年/月/日"
                  />
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    父犬名 <span className="ed-required">※</span>
                  </label>
                  <input
                    name="sire_name"
                    className={`ed-input ${errors.sire_name ? 'is-error' : ''}`}
                    value={formData.sire_name}
                    onChange={handleChange}
                  />
                  {errors.sire_name && <span className="ed-errorText">{errors.sire_name}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    母犬名 <span className="ed-required">※</span>
                  </label>
                  <input
                    name="dam_name"
                    className={`ed-input ${errors.dam_name ? 'is-error' : ''}`}
                    value={formData.dam_name}
                    onChange={handleChange}
                  />
                  {errors.dam_name && <span className="ed-errorText">{errors.dam_name}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    出陳者名 <span className="ed-required">※</span>
                  </label>
                  <input
                    name="exhibitor_name"
                    className={`ed-input ${errors.exhibitor_name ? 'is-error' : ''}`}
                    value={formData.exhibitor_name}
                    onChange={handleChange}
                  />
                  {errors.exhibitor_name && <span className="ed-errorText">{errors.exhibitor_name}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    出陳者都道府県 <span className="ed-required">※</span>
                  </label>
                  <input
                    name="exhibitor_pref"
                    className={`ed-input ${errors.exhibitor_pref ? 'is-error' : ''}`}
                    value={formData.exhibitor_pref}
                    onChange={handleChange}
                  />
                  {errors.exhibitor_pref && <span className="ed-errorText">{errors.exhibitor_pref}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    出陳取扱者名 <span className="ed-required">※</span>
                  </label>
                  <input
                    name="handler_name"
                    className={`ed-input ${errors.handler_name ? 'is-error' : ''}`}
                    value={formData.handler_name}
                    onChange={handleChange}
                  />
                  {errors.handler_name && <span className="ed-errorText">{errors.handler_name}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    出陳取扱者都道府県 <span className="ed-required">※</span>
                  </label>
                  <input
                    name="handler_pref"
                    className={`ed-input ${errors.handler_pref ? 'is-error' : ''}`}
                    value={formData.handler_pref}
                    onChange={handleChange}
                  />
                  {errors.handler_pref && <span className="ed-errorText">{errors.handler_pref}</span>}
                </div>

                <div className="ed-formGroup">
                  <label className="ed-label">
                    チャンピオン証明書 <span className="ed-note">(チャンピオンはチャンピオン証明書を登録しないと申し込みができません。)</span>
                  </label>
                  <div className="ed-fileRow">
                    <div className="ed-fileDisplay">
                      <span className={formData.champion_file_name ? '' : 'ed-datepickerPlaceholder'}>
                        {formData.champion_file_name || 'ファイルを選択してください'}
                      </span>
                      {formData.champion_file_name && (
                        <button
                          type="button"
                          className="ed-datepickerClear"
                          onClick={() => handleChange({ target: { name: 'champion_file_name', value: '' } })}
                          title="クリア"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <label className="btn btn-secondary ed-fileBrowse">
                      参照
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const fileName = file ? file.name : '';
                          handleChange({ target: { name: 'champion_file_name', value: fileName } });
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* フッター */}
            <div className="ed-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setErrors({});
                  setSelectedEntryDogId('');
                }}
                disabled={isSaving}
              >
                キャンセル
              </button>

              <button type="submit" className="btn btn-primary" disabled={isSaving}>
                {isSaving ? '保存中...' : isEdit ? '更新する' : '登録する'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}